// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useGuestRideActions } from '../hooks/useGuestRideActions';
import { useUser } from '@store';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, Button } from '@mui/material';
import { BiSolidInfoCircle } from 'react-icons/bi';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';

// GRAPHQL ---------------------------------------------------------------- //

const REVERT_GUESTRIDE = gql`
  mutation RevertGuestride($id: bigint!, $updated_by: String) {
    update_guestrides_by_pk(pk_columns: { id: $id }, _set: { status: "new", updated_by: $updated_by }) {
      id
      status
    }
  }
`;

// TYPES ---------------------------------------------------------------- //

interface RevertRideModalProps {
  open: boolean;
  onClose: () => void;
  ride: any;
}

// COMPONENT ---------------------------------------------------------------- //

const RevertRideModal: React.FC<RevertRideModalProps> = ({ open, onClose, ride }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { refetch } = useGuestRideActions();
  const user = useUser();

  const [revertGuestride, { loading }] = useMutation(REVERT_GUESTRIDE);

  const handleConfirm = async () => {
    try {
      await revertGuestride({
        variables: {
          id: ride?.id,
          updated_by: user?.profile?.email || null,
        },
      });

      toast.success('Ride reverted');
      if (refetch) refetch();
      onClose();
    } catch (err) {
      console.error('Failed to reactivate ride:', err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: 'revert-ride' },
        extra: { rideId: ride?.id, status: ride?.status },
      });
      toast.error('Failed to reactivate ride');
    }
  };

  return (
    <Modal open={open} handleClose={onClose} width='sm'>
      <ModalHeader handleClose={onClose}>Reactivate Ride</ModalHeader>

      <ModalContent>
        <div className={cls.blurb}>
          <BiSolidInfoCircle className={cls.blurbIcon} />
          <Typography variant='body2' sx={{ textWrap: 'pretty', color: theme.palette.info.dark }}>
            Reactivate the ride for <strong>{ride?.guest_name || 'this guest'}</strong>? Its status will be set back to{' '}
            <strong>New</strong> and it will reappear in the upcoming rides list.
          </Typography>
        </div>

        <Typography variant='body2' color='text.secondary' gutterBottom>
          <strong>Pickup:</strong> {ride?.pickup_address || 'No address provided'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          <strong>Dropoff:</strong> {ride?.dropoff_address || 'No address provided'}
        </Typography>
      </ModalContent>

      <ModalFooter>
        <Button disabled={loading} variant='text' color='utility' onClick={onClose}>
          Go Back
        </Button>
        <Button variant='contained' color='primary' onClick={handleConfirm} disabled={loading}>
          {loading ? 'Reactivating...' : 'Reactivate Ride'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  blurb: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${theme?.palette?.info?.light};
    border-radius: 10px;
    margin-bottom: 12px;
    background-color: ${theme?.palette?.info?.light}20;
  `,
  blurbIcon: css`
    min-width: 20px;
    min-height: 20px;
    font-size: 20px;
    color: ${theme?.palette?.info?.main};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default RevertRideModal;
